// Universal Data Loader for Multiple Data Sources
import Papa from 'papaparse';

/**
 * Load data from sibling folder (F1 data)
 * @param {string} fileName - Name of the file in sibling folder
 * @returns {Promise} - Parsed data
 */
export const loadSiblingData = async (fileName) => {
  try {
    // Load from public/datasets/F1Data folder
    const response = await fetch(`/datasets/F1Data/${fileName}`);
    const text = await response.text();
    
    return new Promise((resolve, reject) => {
      Papa.parse(text, {
        header: true,
        skipEmptyLines: true,
        dynamicTyping: true,
        complete: (results) => {
          resolve(results.data);
        },
        error: (error) => {
          reject(error);
        }
      });
    });
  } catch (error) {
    console.error('Error loading sibling data:', error);
    throw error;
  }
};

/**
 * Load JSON data from sibling folder
 * @param {string} fileName - Name of the JSON file
 * @returns {Promise} - Parsed JSON data
 */
export const loadSiblingJSON = async (fileName) => {
  try {
    const response = await fetch(`/datasets/F1Data/${fileName}`);
    return await response.json();
  } catch (error) {
    console.error('Error loading sibling JSON:', error);
    throw error;
  }
};

/**
 * Load multiple files from sibling folder
 * @param {Array} fileNames - Array of file names
 * @returns {Promise} - Object with parsed data
 */
export const loadMultipleSiblingData = async (fileNames) => {
  try {
    const promises = fileNames.map(async (fileName) => {
      if (fileName.endsWith('.json')) {
        const data = await loadSiblingJSON(fileName);
        return { fileName: fileName.replace('.json', ''), data };
      } else {
        const data = await loadSiblingData(fileName);
        return { fileName: fileName.replace(/\.(csv|txt)$/, ''), data };
      }
    });
    
    const results = await Promise.all(promises);
    
    return results.reduce((acc, { fileName, data }) => {
      acc[fileName] = data;
      return acc;
    }, {});
  } catch (error) {
    console.error('Error loading multiple sibling files:', error);
    throw error;
  }
};

/**
 * Load F1 specific data with common structure
 * @returns {Promise} - F1 data object
 */
export const loadF1Data = async () => {
  try {
    const commonFiles = [
      'drivers.csv',
      'races.csv', 
      'teams.csv',
      'circuits.csv',
      'seasons.csv'
    ];
    
    return await loadMultipleSiblingData(commonFiles);
  } catch (error) {
    console.error('Error loading F1 data:', error);
    throw error;
  }
};
